import { useEffect, useRef, useState } from 'react';
import { Grid, Paper, Divider, Typography, Link, Box } from '@mui/material';
import BreadCrumbs from '../Common/BreadCrumbs';
import PageTitleWithToolTip from '../Common/PageTitleWithTooltip';
import { Select, MultiSelect } from '../Common';
import useBreadCrumbs from '../../hooks/useBreadCrumbs';
import useNavigate from '../../hooks/useNavigate';
import LeakInspectionComplianceCard from './LeakInspectionComplianceCard';
import LeakInspectionSubCard from './LeakInspectionSubCard';
import LeakInspectionLocationTable from './LeakInspectionLocationTable';
import LeakInspectionAssetTable from './LeakInspectionAssetTable';
import Routes from '../../routes';
import { handleMultiSelectChange } from '../../pages/Analytics/utils';
import { trakrefBlue } from '../../color_utils';
import './LeakInspectionsDashboard.css';

const FREQUENCIES = ['Monthly', 'Quarterly', 'Annual'];

const Dashboard = ({ history }) => {
    useNavigate({ pageName: 'Dashboard' });
    useBreadCrumbs({
        path: { id: 'Dashboard', text: 'Dashboard', link: history.location.pathname, reset: true }
    });

    const currentYear = new Date().getFullYear();

    const [filtersLoading, setFiltersLoading] = useState(true);
    const [summaryLoading, setSummaryLoading] = useState(true);

    const [availableYears, setAvailableYears] = useState([currentYear]);
    const [availableRegulations, setAvailableRegulations] = useState([]);

    const [year, setYear] = useState(String(currentYear));
    const [selectedRegulations, setSelectedRegulations] = useState(["-1"]);
    const [debouncedRegulations, setDebouncedRegulations] = useState(["-1"]);
    const debounceRef = useRef(null);

    const [summary, setSummary] = useState(null);
    const [activeFrequency, setActiveFrequency] = useState('Monthly');

    const [selectedLocation, setSelectedLocation] = useState(null);
    const [showTable, setShowTable] = useState(false);

    // ── Load filters ──────────────────────────────────────────────────────────
    useEffect(() => {
        const load = async () => {
            try {
                const res = await fetch(Routes.Dashboard.LeakInspectionFilters());
                const data = await res.json();
                const years = data.years ?? [];
                const regs = data.applicableRegulations ?? [];
                setAvailableYears(years.length > 0 ? years : [currentYear]);
                setAvailableRegulations(regs);
                if (years.length > 0) setYear(String(years[0]));
            } finally {
                setFiltersLoading(false);
            }
        };
        load();
    }, []);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedRegulations(selectedRegulations);
        }, 500);
        return () => clearTimeout(debounceRef.current);
    }, [selectedRegulations]);

    useEffect(() => {
        const load = async () => {
            setSummaryLoading(true);
            try {
                const regFilters = debouncedRegulations.includes("-1") ? [] : debouncedRegulations.map(r => ({ property: 'regulations', value: r }));
                const filters = [
                    { property: 'year', value: year },
                    ...regFilters
                ];
                const res = await fetch(Routes.Dashboard.LeakInspectionSummary(), {
                    method: 'POST',
                    headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                    body: JSON.stringify({ filters })
                });
                const data = await res.json();
                setSummary(data);
            } finally {
                setSummaryLoading(false);
            }
        };
        load();
    }, [year, debouncedRegulations]);

    const handleViewDetails = (frequency) => {
        setActiveFrequency(frequency);
        setSelectedLocation(null);
        setShowTable(true);
    };

    const handleHideDetails = () => {
        setShowTable(false);
        setSelectedLocation(null);
    };

    const handleTabChange = (freq) => {
        setActiveFrequency(freq);
        if (showTable) setSelectedLocation(null);
    };

    const handleLocationClick = (locationName) => setSelectedLocation(locationName);
    const handleBackToLocations = () => setSelectedLocation(null);

    const summaryByFreq = (freq) => {
        if (!summary) return {};
        const prefix = freq.toLowerCase();
        return {
            required: summary[`${prefix}Required`],
            completed: summary[`${prefix}Completed`],
            missed: summary[`${prefix}Missed`],
            complianceRate: summary[`${prefix}ComplianceRate`],
        };
    };

    const yearOptions = availableYears.map(y => ({ id: String(y), name: String(y) }));
    const allOption = { value: "-1", name: "All" };
    const regOptions = [allOption, ...availableRegulations];

    return (
        <>
            <BreadCrumbs path={[{ text: 'Dashboard' }]} />

            <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                {/* ── Title + Subtitle ── */}
                <Box sx={{ px: 3, pt: 3, pb: 3 }}>
                    <PageTitleWithToolTip
                        pageTitle="Leak Inspections Dashboard"
                    />
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                        The Leak Inspections Dashboard provides a real-time summary of required, completed, and missed Monthly, Quarterly, and Annual inspections, with drill-downs to asset level and dynamic filters for applicable regulations and year. It enables users to instantly assess performance, identify risks, and export leak inspection data.
                    </Typography>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={4}>
                            <Select
                                label="Year"
                                value={year}
                                options={yearOptions}
                                onChange={(e) => { setYear(e.target.value); setSelectedLocation(null); }}
                                width={100}
                                disabled={filtersLoading}
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <MultiSelect
                                label="Regulations"
                                value={selectedRegulations}
                                options={regOptions}
                                onChange={(e) => { handleMultiSelectChange(setSelectedRegulations)(e); setSelectedLocation(null); }}
                                width={100}
                                disabled={filtersLoading}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 2, mx: 3 }} />

                {/* ── Cards ── */}
                <Box sx={{ px: 3, py: 3 }}>
                    <Grid container spacing={4} alignItems="stretch">
                        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                            <LeakInspectionComplianceCard summary={summary} loading={summaryLoading} />
                        </Grid>
                        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
                            <LeakInspectionSubCard
                                frequencies={FREQUENCIES.map(freq => ({ name: freq, ...summaryByFreq(freq) }))}
                                loading={summaryLoading}
                                activeFrequency={activeFrequency}
                                onTabChange={handleTabChange}
                                showTable={showTable}
                                onViewDetails={handleViewDetails}
                                onHideDetails={handleHideDetails}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* ── Location / Asset Table ── */}
                {showTable && (
                    <Box sx={{ px: 3, pb: 3 }}>
                        <Divider sx={{ mb: 3 }} />
                        {selectedLocation ? (
                            <LeakInspectionAssetTable
                                year={Number(year)}
                                frequency={activeFrequency}
                                regulations={debouncedRegulations.includes("-1") ? [] : debouncedRegulations}
                                locationName={selectedLocation}
                                onBack={handleBackToLocations}
                            />
                        ) : (
                            <LeakInspectionLocationTable
                                year={Number(year)}
                                frequency={activeFrequency}
                                regulations={debouncedRegulations.includes("-1") ? [] : debouncedRegulations}
                                onLocationClick={handleLocationClick}
                            />
                        )}
                    </Box>
                )}
            </Paper>
        </>
    );
};

export default Dashboard;